import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import CSSModules from 'react-css-modules'
import { Field, SubmissionError, reduxForm } from 'redux-form'
import Isvg from 'react-inlinesvg'

import { localize } from 'helpers/translator'
import { BaseButton, MainButton } from 'components/button'
import TextLink from 'components/text_link/text_link'
import TextInputForm from 'components/input/text/text_input_form'
import CheckboxInputForm from 'components/input/checkbox/checkbox_input_form'
import RecaptchaForm from 'components/recaptcha/recaptcha_form'
import SignUpEmailTermsError from 'views/signup/email/sign_up_email_terms_error'
import { validateEmail } from 'helpers/validations'

import styles from './view.scss'
import googleIcon from 'assets/views/signup/google-icon.svg'
import msIcon from 'assets/views/signup/ms-icon.svg'

const validate = (values, props) => {
	const errors = {}
	if (!values.firstname) {
		errors.firstname = localize('global_form_firstNameIsRequired')
	}
	if (!values.lastname) {
		errors.lastname = localize('global_form_lastNameIsRequired')
	}
	if (!values.email) {
		errors.email = localize('global_form_emailIsRequired')
	} else if (!validateEmail(values.email)) {
		errors.email = localize('global_form_invalidEmail')
	}
	if (!values.password) {
		errors.password = localize('signUp_form_password_error')
	} else {
		props.passwordConditions.validations.forEach(validation => {
			const regex = new RegExp(validation.regex)
			if (!regex.test(values.password)) {
				errors.password = localize('signUp_form_password_error')
			}
		})
	}
	if (!values.terms) {
		errors.terms = localize('signUp_form_mustAcceptTerms2')
	}
	return errors
}

let captcha

class SignupTeacher extends PureComponent {

	getFieldMessage = field => field.error

	getFieldStatus = field => {
		if (field.touched) {
			if (field.invalid) {
				return 'error'
			} else {
				return 'success'
			}
		} else {
			return ''
		}
	}

	getPasswordFieldMessage = field =>
		(field.submitFailed && field.touched && field.invalid)
			? field.error
			: localize('signUp_form_password_error')

	getPasswordFieldStatus = field =>
		(field.submitFailed && field.touched && field.invalid)
			? 'error'
			: 'info'

	formSubmitHandler = values => this.props.submitHandler(values)
		.catch(function(err) {
			captcha.getRenderedComponent().reset()
			const errors = { ...err }
			if (errors.password) {
				errors.password = localize('signUp_form_password_error')
			}
			throw new SubmissionError(errors)
		})

	render() {

		const {
			error,
			handleSubmit,
			submitting,
		} = this.props

		return (
			<div styleName='wrapper'>
				<h1 styleName='title'>{localize('sign_up_teachers_title')}</h1>
				<h2 styleName='subtitle'>{localize('signup_teacher_subtitle')}</h2>
				<div styleName='button'>
					<MainButton
						pill={true}
						onClick={this.props.navigateToGmail}
					>
						<Isvg src={googleIcon} cacheGetRequests={true} styleName='button-icon' />
						{ localize('signUp_signUpWithGoogle_2') }
					</MainButton>
				</div>
				<div styleName='button'>
					<BaseButton
						bgColor='red'
						pill={true}
						fontColor='white'
						onClick={this.props.navigateToOffice}
					>
						<Isvg src={msIcon} cacheGetRequests={true} styleName='button-icon' />
						{ localize('signUp_signUpWithOffice_2') }
					</BaseButton>
				</div>
				<p styleName='separator'>{localize('signup_teacher_separator')}</p>
				<form styleName='form' onSubmit={handleSubmit(this.formSubmitHandler)}>
					<div styleName='form-field'>
						<Field
							name="firstname"
							component={TextInputForm}
							type="text"
							props={{
								placeholder: localize('signUp_form_firstName'),
								disabled: submitting,
								getMessage: this.getFieldMessage,
								fieldStatus: this.getFieldStatus,
								icons: {active: true},
							}}
						/>
					</div>
					<div styleName='form-field'>
						<Field
							name="lastname"
							component={TextInputForm}
							type="text"
							props={{
								placeholder: localize('signUp_form_lastName'),
								disabled: submitting,
								getMessage: this.getFieldMessage,
								fieldStatus: this.getFieldStatus,
								icons: {active: true},
							}}
						/>
					</div>
					<div styleName='form-field'>
						<Field
							name="email"
							component={TextInputForm}
							type="text"
							props={{
								placeholder: localize('signUp_form_schoolEmail'),
								disabled: submitting,
								getMessage: this.getFieldMessage,
								fieldStatus: this.getFieldStatus,
								icons: {active: true},
							}}
						/>
					</div>
					<div styleName='form-field'>
						<Field
							name="password"
							component={TextInputForm}
							type="password"
							props={{
								placeholder: localize('global_form_password'),
								disabled: submitting,
								getMessage: this.getPasswordFieldMessage,
								fieldStatus: this.getPasswordFieldStatus,
								maxLength:
									this.props.passwordConditions.maxLength,
								icons: {active: true},
							}}
						/>
					</div>
					<div styleName='form-captcha'>
						<Field
							component={RecaptchaForm}
							name="recaptcha"
							withRef={true}
							ref={el => {
								captcha = el
							}}
							props={{
								getMessage: this.getFieldMessage,
							}}
						/>
					</div>
					<div styleName='form-disclaimer'>
						<span styleName='form-disclaimer-checkbox'>
							<Field
								name="terms"
								component={CheckboxInputForm}
								props={{
									fieldStatus: this.getFieldStatus,
								}}
							/>
						</span>
						<label styleName='form-disclaimer-text'>
							{localize('signUp_terms_start_2')}
							<TextLink
								styleName='form-disclaimer-link'
								href='https://nearpod.com/terms-conditions'
								target='_blank'
								linkClick={
									this.props.navigateToTerms
								}
							>
								{localize('signUp_terms_link')}
							</TextLink>
							{localize('signUp_terms_end_2')}
						</label>
						<Field
							name="terms"
							component={SignUpEmailTermsError}
							props={{
								style_data: styles.error,
							}}
						/>
					</div>
					<div styleName='form-button'>
						<MainButton
							type='submit'
							pill={true}
							disabled={
								submitting ||
								this.props.form_button_disabled
							}
						>
							{ localize('global_signUp') }
						</MainButton>
					</div>
					{ error &&<div styleName='form-error'>{error}</div>}
				</form>
				<p styleName='text'>
					{localize('signUp_logIn_label_2')}
					<TextLink
						styleName='text-link'
						to={'/login'+location.search}
						linkClick={this.props.navigateToLogin}
					>
						{ localize('global_logIn') }
					</TextLink>
				</p>
			</div>
		)
	}
}

SignupTeacher.propTypes = {
	handleSubmit: PropTypes.func.isRequired,
	submitHandler: PropTypes.func,
	error: PropTypes.string,
	submitting: PropTypes.bool.isRequired,
	form_button_disabled: PropTypes.bool,
	passwordConditions: PropTypes.shape({
		maxLength: PropTypes.string.isRequired,
	}),
	navigateToOffice: PropTypes.func,
	navigateToGmail: PropTypes.func,
	navigateToLogin: PropTypes.func,
	navigateToTerms: PropTypes.func,
}

SignupTeacher.defaultProps = {
	submitHandler: () => {},
	navigateToOffice: () => {},
	navigateToGmail: () => {},
	navigateToLogin: () => {},
	navigateToTerms: () => {},
}

export default (reduxForm({
	form: 'SignupForm',
	fields: ['firstname', 'lastname', 'email', 'password', 'referral', 'terms', 'recaptcha'],
	validate,
})(CSSModules(SignupTeacher, styles, {allowMultiple: true})))
